import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { QUALITY_PRESETS, QUALITY_ORDER } from './mainpage/config';
import { createPhysicsWorld } from './mainpage/physicsSetup';
import { createSparkSystem } from './mainpage/sparkSystem';
import { createSplitScreenRenderer } from './mainpage/splitScreen';
import { createCameraController } from './mainpage/cameraControl';
import { createInputHandlers } from './mainpage/inputHandlers';
import { createCharacterSystem } from './mainpage/characterSpawner';
import { applyPhysicsToSkeleton } from './mainpage/ragdollSystem';
import '../css/MainPage.css';

const MainPage = () => {
    const mountRef = useRef(null);

    useEffect(() => {
        document.body.classList.remove('loading');

        const isUnmountedRef = { value: false };

        // ── Scene ──
        const scene  = new THREE.Scene();
        const width  = window.innerWidth;
        const height = window.innerHeight;

        // ── Quality state ──
        let currentQualityKey = 'ultra';
        let quality = { ...QUALITY_PRESETS[currentQualityKey] };

        // ── Camera ──
        const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
        camera.position.set(0, 2, 5);

        // ── Renderer ──
        const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true, powerPreference: 'high-performance' });
        renderer.setSize(Math.floor(width * quality.renderScale), Math.floor(height * quality.renderScale), false);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, quality.pixelRatioCap));
        renderer.setClearColor(0x0a0a10, 1);
        renderer.toneMapping         = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.0;
        renderer.outputColorSpace    = THREE.SRGBColorSpace;
        renderer.shadowMap.enabled   = true;
        renderer.shadowMap.type      = THREE.PCFSoftShadowMap;
        renderer.shadowMap.autoUpdate = true;
        renderer.domElement.style.cssText = 'display:block;width:100%;height:100%;position:absolute;top:0;left:0;touch-action:none;';

        const currentMount = mountRef.current;
        if (!currentMount) return;
        while (currentMount.firstChild) currentMount.removeChild(currentMount.firstChild);
        currentMount.appendChild(renderer.domElement);

        // ── Lighting ──
        scene.add(new THREE.AmbientLight(0xffeeb0, 0.1));

        const dirLight = new THREE.DirectionalLight(0xfff5e6, 2.5);
        dirLight.position.set(5, 8, -5);
        dirLight.castShadow = true;
        dirLight.shadow.mapSize.width  = 512;
        dirLight.shadow.mapSize.height = 512;
        dirLight.shadow.camera.near   = 0.5;
        dirLight.shadow.camera.far    = 20;
        dirLight.shadow.camera.left   = -5;
        dirLight.shadow.camera.right  = 5;
        dirLight.shadow.camera.top    = 5;
        dirLight.shadow.camera.bottom = -5;
        dirLight.shadow.bias = -0.001;
        scene.add(dirLight);

        scene.add(new THREE.HemisphereLight(0x8a9ba8, 0x3d3126, 0.8));

        // ── Debug bodies flag ──
        const showDebugBodies = false;

        const boxMesh = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshStandardMaterial({ color: 0x00ff00 }));
        boxMesh.position.set(-2, 0.5, 0);
        boxMesh.visible = showDebugBodies;
        scene.add(boxMesh);

        // ── Physics world ──
        const { world, floorPhysicsBody, floorDebugMesh, floorGeo, floorMat } = createPhysicsWorld(scene);
        // More solver iterations → stable ragdoll constraints
        world.solver.iterations = 30;
        world.solver.tolerance  = 0.001;

        // Debug capsule (independent of character instances)
        const charRadius = 0.4;
        const charHeight = 0.8;
        const charMesh   = new THREE.Mesh(
            new THREE.CapsuleGeometry(charRadius, charHeight, 4, 16),
            new THREE.MeshStandardMaterial({ color: 0xff0055 })
        );
        charMesh.castShadow = true;
        charMesh.visible    = showDebugBodies;
        scene.add(charMesh);

        const charShape  = new CANNON.Cylinder(charRadius, charRadius, charHeight, 16);
        const charPBody  = new CANNON.Body({ mass: 5, position: new CANNON.Vec3(0, 3, 0) });
        const cylQuat    = new CANNON.Quaternion();
        cylQuat.setFromAxisAngle(new CANNON.Vec3(1, 0, 0), Math.PI / 2);
        charPBody.addShape(charShape, new CANNON.Vec3(0, 0, 0), cylQuat);
        world.addBody(charPBody);

        // ── Spark / 3DGS ──
        const sparkSys = createSparkSystem({ renderer, scene, camera, isUnmountedRef });
        const { sparkRenderer } = sparkSys;
        window.mySparkRenderer  = sparkRenderer;
        window.myScene  = scene;
        window.myCamera = camera;

        // ── Camera controller ──
        const cameraCtrl = createCameraController({
            camera,
            sparkRenderer,
            onSparkDirty: () => sparkSys.markDirty(),
        });
        cameraCtrl.applyFixedCameraPose();
        cameraCtrl.registerWindowExports();

        // ── Split-screen renderer ──
        const splitScreen = createSplitScreenRenderer({ renderer, scene, camera, width, height });

        // ── Quality helpers ──
        const applyQualityPreset = (presetKey) => {
            const preset = QUALITY_PRESETS[presetKey];
            if (!preset) return;
            currentQualityKey = presetKey;
            quality = { ...preset };

            renderer.setSize(
                Math.floor(window.innerWidth  * quality.renderScale),
                Math.floor(window.innerHeight * quality.renderScale),
                false
            );
            renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, quality.pixelRatioCap));
            sparkRenderer.minSortIntervalMs = quality.minSortIntervalMs;
            sparkRenderer.maxPixelRadius    = quality.maxPixelRadius;
            sparkRenderer.clipXY            = quality.clipXY;
            sparkSys.markDirty();
            sparkRenderer.setDirty();
            console.info(`[Dojo] quality preset set to: ${presetKey}`);
        };

        applyQualityPreset(currentQualityKey);

        window.setDojoQuality       = applyQualityPreset;
        window.getDojoQuality       = () => currentQualityKey;
        window.getDojoQualityPresets = () => [...QUALITY_ORDER];
        window.getDojoLoadStats     = () => ({ ...sparkSys.getLoadStats(), activeQuality: currentQualityKey });

        // ── Character instances (shared array) ──
        const characterInstances = [];
        const charSys = createCharacterSystem({ scene, world, characterInstances, isUnmountedRef });
        charSys.loadCharacters();

        // ── Input handlers ──
        const input = createInputHandlers({ camera, renderer, characterInstances, cameraController: cameraCtrl, world });
        input.attach();

        // ── Start splat loading ──
        sparkSys.loadDojo();

        // ── Clock ──
        const clock = new THREE.Clock();

        // ── Animate loop ──
        const animate = () => {
            const dt = clock.getDelta();

            try {
                // Step physics whenever any character is in ragdoll mode or debug is on
                const anyRagdoll = characterInstances.some(i => i.isRagdollActive);
                if ((showDebugBodies || anyRagdoll) && dt > 0) world.step(1 / 60, dt, 3);

                if (showDebugBodies) {
                    charMesh.position.copy(charPBody.position);
                    charMesh.quaternion.copy(charPBody.quaternion);
                }

                const _rwp = new THREE.Vector3();
                const _wp  = new THREE.Vector3();
                const _wq  = new THREE.Quaternion();

                for (const inst of characterInstances) {
                    if (inst.isRagdollActive) {
                        // ── RAGDOLL MODE ──
                        // Physics drives everything. Sync bones from physics bodies,
                        // then update debug mesh positions from physics.
                        applyPhysicsToSkeleton(inst);

                        for (const { body, debugMesh } of inst.ragdollBodies) {
                            if (debugMesh) {
                                debugMesh.position.set(body.position.x, body.position.y, body.position.z);
                                debugMesh.quaternion.set(body.quaternion.x, body.quaternion.y, body.quaternion.z, body.quaternion.w);
                            }
                        }
                    } else {
                        // ── ANIMATION MODE ──
                        // mainBody stays KINEMATIC so physics stepping (triggered by any
                        // active ragdoll) can't apply gravity/collisions to this character.
                        // model.position is the source of truth; mainBody just tracks it.
                        inst.mainBody.type = CANNON.Body.KINEMATIC;

                        // 1. Capture root bone world XZ BEFORE animation update.
                        // World-space before/after avoids the model.rotation.x=-PI/2 trap:
                        // bone local Y (~100 FBX cm) maps to world -Z, so using local coords
                        // would produce an ~11-unit jump on the first frame.
                        let prevRootWorldX = 0, prevRootWorldZ = 0;
                        if (inst.rootBone && !inst.isAnimationPaused) {
                            inst.rootBone.getWorldPosition(_rwp);
                            prevRootWorldX = _rwp.x;
                            prevRootWorldZ = _rwp.z;
                        }

                        // 2. Advance animation
                        if (inst.mixer && !inst.isAnimationPaused) {
                            inst.mixer.update(dt);
                        }

                        // 3. Root motion extraction – per-frame world XZ delta
                        if (inst.rootBone && !inst.isAnimationPaused) {
                            inst.rootBone.getWorldPosition(_rwp);
                            inst.model.position.x += _rwp.x - prevRootWorldX;
                            inst.model.position.z += _rwp.z - prevRootWorldZ;
                        }

                        // Keep mainBody in sync so drag raycasting stays accurate
                        inst.mainBody.position.set(
                            inst.model.position.x,
                            inst.model.position.y,
                            inst.model.position.z
                        );

                        // 4. Kinematic ragdoll bodies track bone world transforms
                        if (inst.ragdollBodies) {
                            for (const { bone, body, debugMesh } of inst.ragdollBodies) {
                                bone.getWorldPosition(_wp);
                                bone.getWorldQuaternion(_wq);
                                body.position.set(_wp.x, _wp.y, _wp.z);
                                body.quaternion.set(_wq.x, _wq.y, _wq.z, _wq.w);
                                if (debugMesh) {
                                    debugMesh.position.copy(_wp);
                                    debugMesh.quaternion.copy(_wq);
                                }
                            }
                        }
                    }
                }

                // Split-screen render
                splitScreen.renderFrame({
                    sparkRenderer,
                    characterInstances,
                    floorDebugMesh,
                    showDebugBodies,
                    charMesh,
                    elapsedTime: clock.elapsedTime,
                    driveSparkUpdate:           sparkSys.driveSparkUpdate.bind(sparkSys),
                    qualitySparkUpdateIntervalMs: quality.sparkUpdateIntervalMs,
                });

            } catch (err) {
                console.error('Render loop error:', err);
            }
        };
        renderer.setAnimationLoop(animate);

        // ── Resize ──
        const onResize = () => {
            const newW = Math.floor(window.innerWidth  * quality.renderScale);
            const newH = Math.floor(window.innerHeight * quality.renderScale);
            camera.aspect = window.innerWidth / window.innerHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(newW, newH, false);
            renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, quality.pixelRatioCap));
            splitScreen.onResize(newW, newH);
            sparkSys.markDirty();
        };
        window.addEventListener('resize', onResize);

        // ── Quality hotkeys (1-5) ──
        const onQualityHotkey = (event) => {
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            const index = Number.parseInt(event.key, 10);
            if (!Number.isInteger(index) || index < 1 || index > QUALITY_ORDER.length) return;
            applyQualityPreset(QUALITY_ORDER[index - 1]);
        };
        window.addEventListener('keydown', onQualityHotkey);

        const banner = document.querySelector('.vAygCK-api-load-alpha-banner');
        if (banner) banner.style.display = 'none';

        // ── Cleanup ──
        return () => {
            isUnmountedRef.value = true;

            charSys.dispose();

            scene.remove(floorDebugMesh);
            floorGeo.dispose();
            floorMat.dispose();
            world.removeBody(floorPhysicsBody);
            delete window.myFloor;

            input.detach();
            cameraCtrl.unregisterWindowExports();

            window.removeEventListener('resize',  onResize);
            window.removeEventListener('keydown', onQualityHotkey);

            renderer.setAnimationLoop(null);
            sparkSys.dispose();
            renderer.dispose();

            delete window.myScene;
            delete window.myCamera;
            delete window.setDojoQuality;
            delete window.getDojoQuality;
            delete window.getDojoQualityPresets;
            delete window.getDojoLoadStats;

            if (currentMount && renderer.domElement && currentMount.contains(renderer.domElement)) {
                currentMount.removeChild(renderer.domElement);
            }
        };
    }, []);

    return (
        <div style={{ width: '100vw', height: '100vh', position: 'fixed', top: 0, left: 0, overflow: 'hidden', zIndex: 0, backgroundColor: '#0a0a10' }}>
            <div ref={mountRef} style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, zIndex: 999 }} />
        </div>
    );
};

export default MainPage;
